import { useEffect, useState } from "react";
import { apiClient } from "../../App";
import { Users, TrendingUp, DollarSign, Sparkles, Activity } from "lucide-react";

/**
 * Overview — scalar cards + two daily series charts (signups, generations).
 *
 * No chart library is installed; we render lightweight SVG sparklines ourselves
 * (spec said "use whatever chart lib is already in the project; don't add a new one").
 *
 * Strict readiness checks: `typeof data === "object" && data !== null` before
 * touching any field. No truthy-only shortcuts.
 */

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/admin/metrics/overview")
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || "Failed to load metrics");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="p-6 md:p-10" data-testid="admin-overview-error">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">
          {error}
        </div>
      </div>
    );
  }

  const ready = typeof data === "object" && data !== null && typeof data.total_users === "number";

  if (!ready) {
    return <OverviewSkeleton />;
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="admin-overview">
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Overview</h1>
        <p className="text-sm text-slate-500 mt-1">Snapshot of users, generations, and revenue.</p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Total users"
          value={data.total_users}
          sub={`+${data.new_users_last_7d} in 7d · +${data.new_users_last_30d} in 30d`}
          testId="admin-stat-total-users"
        />
        <StatCard
          icon={<DollarSign className="w-5 h-5" />}
          label="MRR (est.)"
          value={`$${(data.mrr_estimate ?? 0).toLocaleString()}`}
          sub={`${sumTierPaid(data.users_by_tier)} paid subscribers`}
          testId="admin-stat-mrr"
        />
        <StatCard
          icon={<Sparkles className="w-5 h-5" />}
          label="Generations"
          value={data.total_generations_all_time}
          sub={`${data.generations_last_7d} in last 7d`}
          testId="admin-stat-generations"
        />
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          label="Payments (30d)"
          value={data.payments_last_30d?.completed ?? 0}
          sub={`${data.payments_last_30d?.pending ?? 0} pending · ${
            data.payments_last_30d?.failed ?? 0
          } failed`}
          testId="admin-stat-payments"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Signups — last 30 days" series={data.signups_daily} accent="#8B5CF6" />
        <ChartCard title="Generations — last 30 days" series={data.generations_daily} accent="#EC4899" />
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Users by tier</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.users_by_tier || {}).map(([tier, count]) => (
            <div key={tier} className="bg-slate-50 rounded-lg p-3" data-testid={`admin-tier-${tier}`}>
              <div className="text-xs uppercase text-slate-500 tracking-wide">{tier}</div>
              <div className="text-xl font-bold text-slate-900 mt-1">{count}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function sumTierPaid(byTier) {
  if (!byTier || typeof byTier !== "object") return 0;
  return Object.entries(byTier)
    .filter(([k]) => k !== "free")
    .reduce((a, [, v]) => a + (typeof v === "number" ? v : 0), 0);
}

function StatCard({ icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-slate-400">{icon}</span>
        <TrendingUp className="w-4 h-4 text-slate-300" />
      </div>
      <div className="mt-3 text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-1 truncate">{sub}</div>
    </div>
  );
}

function ChartCard({ title, series, accent }) {
  const safe = Array.isArray(series) ? series : [];
  const max = Math.max(1, ...safe.map((p) => (typeof p?.count === "number" ? p.count : 0)));
  const total = safe.reduce((a, p) => a + (p?.count || 0), 0);
  const w = 600;
  const h = 140;
  const pad = 16;

  const points = safe.map((p, i) => {
    const x = pad + (i * (w - pad * 2)) / Math.max(1, safe.length - 1);
    const y = h - pad - ((p.count || 0) / max) * (h - pad * 2);
    return `${x},${y}`;
  });
  const path = points.length ? `M${points.join(" L")}` : "";

  // Area fill (polygon closing to baseline)
  const area =
    points.length > 0
      ? `${path} L${pad + (w - pad * 2)},${h - pad} L${pad},${h - pad} Z`
      : "";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        <span className="text-xs text-slate-500">{total} total</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-36">
        <defs>
          <linearGradient id={`grad-${accent}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.25" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* baseline */}
        <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="#E2E8F0" strokeWidth="1" />
        {area && <path d={area} fill={`url(#grad-${accent})`} />}
        {path && <path d={path} fill="none" stroke={accent} strokeWidth="2" strokeLinejoin="round" />}
        {points.map((p, i) => {
          const [x, y] = p.split(",");
          return <circle key={i} cx={x} cy={y} r="2" fill={accent} />;
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
        <span>{safe[0]?.date?.slice(5)}</span>
        <span>{safe[safe.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="admin-overview-loading">
      <div className="h-8 w-40 bg-slate-200 rounded animate-pulse mb-2" />
      <div className="h-4 w-80 bg-slate-200 rounded animate-pulse mb-8" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-28 bg-white border border-slate-200 rounded-xl animate-pulse" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {[1, 2].map((i) => (
          <div key={i} className="h-56 bg-white border border-slate-200 rounded-xl animate-pulse" />
        ))}
      </div>
      <div className="h-32 bg-white border border-slate-200 rounded-xl animate-pulse" />
    </div>
  );
}
